/**
 * Account detail — one account's per-bucket balances and its ledger history.
 *
 * Balances **and** each bucket's lifecycle status come from the single-row
 * `GET /accounts/list?account_id=<id>` query (design D17); this page never calls
 * `GET /accounts/<id>/balance`, which returns no lifecycle status and therefore
 * could not serve the card on a direct open or a reload. That query's row count
 * is also the page's whole successful state machine (design D15): zero rows is
 * the no-data state (an unknown id and another tenant's account, rendered
 * identically), one row is the account — even when it holds no bucket at all.
 *
 * The ledger table reads the operator endpoint, which carries the audit fields
 * the merchant ledger deliberately withholds. A `403 application_forbidden` from
 * it is a failed request and is rendered as the server's error, never as an
 * empty state.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/useToast'
import { formatAmount } from '@/pages/accounts/amounts'
import {
  readStoredApplicationId,
} from '@/pages/accounts/applicationContext'
import {
  fetchAccountList,
  fetchOperatorLedger,
  serverErrorMessage,
  type AccountRow,
  type OperatorLedgerRow,
} from '@/pages/accounts/accountsApi'
import type { DetailReloadResult } from '@/pages/accounts/provisionSession'
import {
  resolveBalanceArea,
  resolveDetailState,
  resolveLedgerArea,
} from '@/pages/accounts/detailState'
import {
  DIRECTIONS,
  ENTRY_TYPES,
  PRESET_VIEWS,
  ledgerFilterParams,
  viewParam,
  type PresetView,
} from '@/pages/accounts/ledgerViews'
import { shouldApplyResponse, tagRequest } from '@/pages/accounts/requestContext'
import { GrantDialog, ReversalDialog } from '@/pages/accounts/writeDialogs'

const LEDGER_PER_PAGE = 20

export default function AccountDetailPage() {
  const { accountId = '' } = useParams<{ accountId: string }>()
  const { hasRole } = useAuth()
  const { toast } = useToast()
  const isAdmin = hasRole('admin')

  const storage = typeof window === 'undefined' ? null : window.localStorage
  const [applicationId] = useState<number | null>(() => readStoredApplicationId(storage))

  const [row, setRow] = useState<AccountRow | null>(null)
  const [rowCount, setRowCount] = useState(0)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailLoaded, setDetailLoaded] = useState(false)

  const [entries, setEntries] = useState<OperatorLedgerRow[]>([])
  const [ledgerTotal, setLedgerTotal] = useState(0)
  const [ledgerError, setLedgerError] = useState<string | null>(null)
  const [ledgerLoading, setLedgerLoading] = useState(false)
  const [ledgerLoaded, setLedgerLoaded] = useState(false)

  const [preset, setPreset] = useState<PresetView>('all')
  const [entryType, setEntryType] = useState('')
  const [direction, setDirection] = useState('')
  const [createdAfter, setCreatedAfter] = useState('')
  const [createdBefore, setCreatedBefore] = useState('')
  const [page, setPage] = useState(1)

  const [grantOpen, setGrantOpen] = useState(false)
  const [reversalOpen, setReversalOpen] = useState(false)

  const detailGeneration = useRef(0)
  const ledgerGeneration = useRef(0)

  const filters = useMemo(
    () => ledgerFilterParams({ entryType, direction, createdAfter, createdBefore }),
    [entryType, direction, createdAfter, createdBefore]
  )

  /**
   * The single-row account query. It also **returns** its outcome, because the
   * grant dialog's post-provisioning confirmation reads the reloaded row rather
   * than the provisioning response (provision-sub-account-from-grant-dialog,
   * design D6) and must tell a failed reload from one carrying no usable
   * bucket. The page's own state handling is unchanged.
   */
  const loadDetail = useCallback(async (): Promise<DetailReloadResult> => {
    if (applicationId == null || !accountId) {
      return { ok: false, message: '当前没有选定的应用或账户' }
    }
    const context = {
      applicationId, accountId, view: null, filters: {}, page: 1,
    }
    const tag = tagRequest(context, detailGeneration.current)
    detailGeneration.current = tag.generation
    setDetailLoading(true)
    setDetailError(null)
    try {
      const envelope = await fetchAccountList(applicationId, { account_id: accountId })
      if (!shouldApplyResponse(tag, context, detailGeneration.current)) {
        return { ok: false, message: '该请求已被更新的请求取代' }
      }
      setRow(envelope.accounts[0] ?? null)
      setRowCount(envelope.accounts.length)
      setDetailLoaded(true)
      return { ok: true, row: envelope.accounts[0] ?? null }
    } catch (err: unknown) {
      const message = serverErrorMessage(err, 'Failed to load the account')
      if (tag.generation !== detailGeneration.current) return { ok: false, message }
      setDetailError(message)
      setRow(null)
      setRowCount(0)
      setDetailLoaded(false)
      return { ok: false, message }
    } finally {
      if (tag.generation === detailGeneration.current) setDetailLoading(false)
    }
  }, [accountId, applicationId])

  const loadLedger = useCallback(async () => {
    if (applicationId == null || !accountId) return
    const view = viewParam(preset)
    const context = { applicationId, accountId, view, filters, page }
    const tag = tagRequest(context, ledgerGeneration.current)
    ledgerGeneration.current = tag.generation
    setLedgerLoading(true)
    setLedgerError(null)
    try {
      const params: Record<string, string | number> = {
        ...filters, page, per_page: LEDGER_PER_PAGE,
      }
      if (view) params.view = view
      const envelope = await fetchOperatorLedger(applicationId, accountId, params)
      if (!shouldApplyResponse(tag, context, ledgerGeneration.current)) return
      setEntries(envelope.entries)
      setLedgerTotal(envelope.total)
      setLedgerLoaded(true)
    } catch (err: unknown) {
      if (tag.generation !== ledgerGeneration.current) return
      setLedgerError(serverErrorMessage(err, 'Failed to load the ledger'))
      setEntries([])
      setLedgerLoaded(false)
    } finally {
      if (tag.generation === ledgerGeneration.current) setLedgerLoading(false)
    }
  }, [accountId, applicationId, filters, page, preset])

  useEffect(() => { loadDetail() }, [loadDetail])
  useEffect(() => { loadLedger() }, [loadLedger])

  // round-1 fix (F1): this route is REUSED across account ids — browser
  // back/forward between two account detail views re-renders this component
  // with a different `accountId` instead of remounting it. An open write
  // dialog must not survive that: its typed amount, its idempotency key and
  // its target were all bound to the account the operator was looking at.
  // Closing here (plus the dialogs' own target guard and the `key` below)
  // makes a navigation destroy the dialog rather than re-aim it.
  const dialogTargetRef = useRef(accountId)
  useEffect(() => {
    if (dialogTargetRef.current === accountId) return
    dialogTargetRef.current = accountId
    setGrantOpen(false)
    setReversalOpen(false)
  }, [accountId])

  const refresh = useCallback(() => {
    loadDetail()
    loadLedger()
  }, [loadDetail, loadLedger])

  const detailState = resolveDetailState({
    loading: detailLoading,
    error: detailError,
    loaded: detailLoaded,
    rowCount,
  })
  const ledgerArea = resolveLedgerArea({
    loading: ledgerLoading,
    error: ledgerError,
    loaded: ledgerLoaded,
    rowCount: entries.length,
  })
  const ledgerPages = Math.max(1, Math.ceil(ledgerTotal / LEDGER_PER_PAGE))
  // Grant targets are every `active` bucket, of BOTH asset kinds
  // (grant-dialog-monetary-bonus-target D3): the server refuses a grant into a
  // frozen or closed bucket, and an account holding only such a bucket must
  // still reach the dialog's provisioning step. The asset-kind grouping,
  // ordering and default live in `grantTargets.ts`, not here.
  const heldSubAccounts = row?.sub_accounts ?? []
  const activeBuckets = heldSubAccounts.filter((b) => b.status === 'active')
  const dialogOpen = grantOpen || reversalOpen

  if (applicationId == null) {
    return (
      <main className="space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight">Account</h1>
        <p className="text-sm text-muted-foreground">
          先在 <Link className="underline" to="/accounts">Accounts</Link> 页选择一个应用，
          再打开账户详情：账户按应用隔离读取。
        </p>
      </main>
    )
  }

  return (
    <main className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Account</h1>
          <p className="font-mono text-xs text-muted-foreground">{accountId}</p>
        </div>
        {isAdmin && detailState === 'account' && (
          <div className="flex gap-2">
            <Button onClick={() => setGrantOpen(true)} disabled={dialogOpen}>赠送余额</Button>
            <Button variant="outline" onClick={() => setReversalOpen(true)} disabled={dialogOpen}>
              回收赠送
            </Button>
          </div>
        )}
      </header>

      {detailError && (
        <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {detailError}
        </div>
      )}

      <section aria-labelledby="balances-heading" className="rounded-lg border p-5">
        <h2 id="balances-heading" className="mb-4 text-lg font-medium">Balances</h2>
        {detailState === 'error' ? (
          <p className="text-sm text-red-700">无法读取该账户 — 见上方错误。</p>
        ) : detailState === 'loading' ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : detailState === 'no-data' ? (
          <p className="text-sm text-muted-foreground">
            当前应用下没有这个 id 的账户。
          </p>
        ) : resolveBalanceArea(row?.sub_accounts.length ?? 0) === 'no-buckets' ? (
          <p className="text-sm text-muted-foreground">尚未开通任何子账户</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {row?.sub_accounts.map((bucket) => (
              <div key={bucket.id} className="rounded-md border p-4">
                <div className="text-sm font-medium">{bucket.type}</div>
                <div className="text-xs text-muted-foreground">
                  {bucket.asset_kind}{bucket.currency ? ` · ${bucket.currency}` : ''} · {bucket.status}
                </div>
                <dl className="mt-2 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Balance</dt>
                    <dd className="font-mono">{formatAmount(bucket.balance, bucket)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Available</dt>
                    <dd className="font-mono">{formatAmount(bucket.available, bucket)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Pending</dt>
                    <dd className="font-mono">{formatAmount(bucket.pending, bucket)}</dd>
                  </div>
                </dl>
              </div>
            ))}
          </div>
        )}
      </section>

      <section aria-labelledby="ledger-heading" className="rounded-lg border p-5">
        <h2 id="ledger-heading" className="mb-4 text-lg font-medium">
          Ledger{ledgerLoaded ? ` (${ledgerTotal})` : ''}
        </h2>

        <div className="mb-4 flex flex-wrap gap-2">
          {PRESET_VIEWS.map((view) => (
            <button
              key={view.id}
              className={`rounded-md border px-3 py-1 text-sm ${
                preset === view.id ? 'bg-primary text-primary-foreground' : 'hover:bg-slate-50'
              }`}
              onClick={() => { setPage(1); setPreset(view.id) }}
              disabled={dialogOpen}
            >
              {view.label}
            </button>
          ))}
        </div>

        <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Entry type</span>
            <select
              className="w-full rounded-md border px-3 py-2"
              value={entryType}
              onChange={(e) => { setPage(1); setEntryType(e.target.value) }}
            >
              <option value="">All</option>
              {ENTRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Direction</span>
            <select
              className="w-full rounded-md border px-3 py-2"
              value={direction}
              onChange={(e) => { setPage(1); setDirection(e.target.value) }}
            >
              <option value="">All</option>
              {DIRECTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Created after</span>
            <input
              type="datetime-local"
              className="w-full rounded-md border px-3 py-2"
              value={createdAfter}
              onChange={(e) => { setPage(1); setCreatedAfter(e.target.value) }}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Created before</span>
            <input
              type="datetime-local"
              className="w-full rounded-md border px-3 py-2"
              value={createdBefore}
              onChange={(e) => { setPage(1); setCreatedBefore(e.target.value) }}
            />
          </label>
        </div>

        {ledgerError && (
          <div role="alert" className="mb-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {ledgerError}
          </div>
        )}

        {ledgerArea === 'error' ? (
          <p className="text-sm text-red-700">无法读取流水 — 见上方错误。</p>
        ) : ledgerArea === 'loading' ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : ledgerArea === 'empty' ? (
          <p className="text-sm text-muted-foreground">没有符合条件的流水。</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Time</th>
                  <th className="py-2 pr-4 font-medium">Type</th>
                  <th className="py-2 pr-4 font-medium">Amount</th>
                  <th className="py-2 pr-4 font-medium">Before → After</th>
                  <th className="py-2 pr-4 font-medium">Operator</th>
                  <th className="py-2 pr-4 font-medium">Reason</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id} className="border-b last:border-0">
                    <td className="py-2 pr-4 text-muted-foreground">
                      {new Date(entry.created_at).toLocaleString()}
                    </td>
                    <td className="py-2 pr-4">
                      {entry.entry_type} / {entry.direction}
                      {entry.is_bonus_grant && (
                        <span className="ml-2 rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-xs text-emerald-700">
                          bonus grant
                        </span>
                      )}
                      {/*
                        A top-up row carrying a payment order id navigates to
                        that order on the Payments page (payment orders are a
                        different table from commerce orders, and have no detail
                        route of their own). A row without an id renders no link
                        at all rather than a dead one.
                      */}
                      {entry.entry_type === 'topup' && entry.payment_order_id && (
                        <Link
                          className="ml-2 text-xs underline"
                          to={`/payments?order_id=${encodeURIComponent(entry.payment_order_id)}`}
                        >
                          payment order
                        </Link>
                      )}
                    </td>
                    <td className="py-2 pr-4 font-mono">
                      {formatAmount(entry.amount, entry)}
                    </td>
                    <td className="py-2 pr-4 font-mono text-xs">
                      {entry.balance_before == null ? '—' : formatAmount(entry.balance_before, entry)}
                      {' → '}
                      {entry.balance_after == null ? '—' : formatAmount(entry.balance_after, entry)}
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {entry.operator_id == null ? 'system' : `user #${entry.operator_id}`}
                    </td>
                    <td className="py-2 pr-4">{entry.description ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {ledgerLoaded && ledgerPages > 1 && (
          <div className="mt-4 flex items-center gap-3 text-sm">
            <button
              className="rounded-md border px-3 py-1 transition-colors hover:bg-slate-50 disabled:opacity-50"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || dialogOpen}
            >
              Previous
            </button>
            <span className="text-muted-foreground">Page {page} of {ledgerPages}</span>
            <button
              className="rounded-md border px-3 py-1 transition-colors hover:bg-slate-50 disabled:opacity-50"
              onClick={() => setPage((p) => Math.min(ledgerPages, p + 1))}
              disabled={page >= ledgerPages || dialogOpen}
            >
              Next
            </button>
          </div>
        )}
      </section>

      {isAdmin && grantOpen && (
        <GrantDialog
          key={`grant-${applicationId}-${accountId}`}
          open={grantOpen}
          applicationId={applicationId}
          accountId={accountId}
          activeBuckets={activeBuckets}
          heldSubAccounts={heldSubAccounts}
          reloadDetail={loadDetail}
          onClose={() => setGrantOpen(false)}
          onRefresh={refresh}
          notify={(message) => toast({ title: '赠送已提交', description: message })}
        />
      )}
      {isAdmin && reversalOpen && (
        <ReversalDialog
          key={`reversal-${applicationId}-${accountId}`}
          open={reversalOpen}
          applicationId={applicationId}
          accountId={accountId}
          activeBuckets={activeBuckets}
          onClose={() => setReversalOpen(false)}
          onRefresh={refresh}
          notify={(message) => toast({ title: '回收结果', description: message })}
        />
      )}
    </main>
  )
}
